<template>
    <section class="request-details">
  
        <header class="request-toolbar">

        <!-- <div class="toolbar-copy">
        <h3>
            Attorney Requests
        </h3>

        <p>
            Review requests submitted through Maix, Watchlist,
            and direct attorney-request flows.
        </p>
        </div> -->

        <!-- <div class="search-row">
        <div class="request-search">
            <span class="material-icons-outlined">
            search
            </span>

            <input
            v-model="searchText"
            type="search"
            placeholder="Search requests"
            />
        </div>
        </div> -->

        <div class="toolbar-controls">

        <label class="sort-control">
            <span>
            Date
            </span>

            <select v-model="dateSort">
            <option value="newest">
                Newest first
            </option>

            <option value="oldest">
                Oldest first
            </option>
            </select>
        </label>

        <label class="sort-control">
            <span>
            Status
            </span>

            <select v-model="statusFilter">
            <option value="">
                All statuses
            </option>

            <option
                v-for="status in availableStatuses"
                :key="status.value"
                :value="status.value"
            >
                {{ status.label }}
            </option>
            </select>
        </label>

        <label class="sort-control">
            <span>
            Updated
            </span>

            <select v-model="activitySort">
            <option value="">
                Ignore updated date
            </option>

            <option value="recent">
                Recently updated
            </option>

            <option value="oldest">
                Least recently updated
            </option>
            </select>
        </label>

        <button
            type="button"
            class="refresh-button"
            :class="{ 'refresh-button--filtered': hasActiveFilters }"
            :disabled="loading"
            @click="loadRequests"
        >
            {{ loading ? "Loading..." : "Refresh" }}
        </button>

        </div>

        </header>
  
      <section
        v-if="!loading && requests.length > 0"
        class="status-summary"
      >
  
        <button
          v-for="summary in statusSummaries"
          :key="summary.status"
          type="button"
          class="summary-card"
          :class="{
            active:
              statusFilter === summary.status
          }"
          @click="toggleStatusFilter(summary.status)"
        >
          <span class="summary-label">
            {{ statusLabel(summary.status) }}
          </span>
  
          <strong class="summary-count">
            {{ summary.count }}
          </strong>
        </button>
  
        <button
          v-if="statusFilter"
          type="button"
          class="clear-filter-button"
          @click="statusFilter = ''"
        >
          Clear filter
        </button>
  
      </section>
  
      <div
        v-if="loading"
        class="state-card"
      >
        Loading attorney requests...
      </div>
  
      <div
        v-else-if="errorMessage"
        class="state-card error"
      >
        <strong>
          Unable to load attorney requests.
        </strong>
  
        <span>
          {{ errorMessage }}
        </span>
      </div>
  
      <div
        v-else-if="requests.length === 0"
        class="state-card empty"
      >
        <div class="empty-icon">
          <span class="material-icons-outlined">
            inbox
          </span>
        </div>
  
        <div>
          <h4>
            No attorney requests
          </h4>
  
          <p>
            New requests will appear here after a client submits one.
          </p>
        </div>
      </div>
  
      <div
        v-else-if="displayedRequests.length === 0"
        class="state-card empty"
      >
        No requests match the selected status.
      </div>
  
      <div
        v-else
        class="request-table-wrapper"
      >
        <table class="request-table">
  
            <thead>
                <tr>
                    <th>
                    Actions
                    </th>

                    <th>
                    Activity
                    </th>

                    <th>
                    Status
                    </th>

                    <th>
                    Jurisdiction
                    </th>

                    <th>
                    Issues
                    </th>

                    <th>
                    Client
                    </th>
                </tr>
                </thead>
  
          <tbody>
            <tr
                v-for="request in displayedRequests"
                :key="request.id"
                >
                <td class="actions-column">
                    <button
                    type="button"
                    class="review-button"
                    @click="$emit('review', request)"
                    >
                    Review
                    </button>
                </td>

                <td class="activity-column">
                    <div class="activity-block">
                    <span class="activity-label">
                        Received
                    </span>

                    <strong>
                        {{ relativeDate(request.createdAt) }}
                    </strong>

                    <span class="exact-date">
                        {{ formatCompactDate(request.createdAt) }}
                    </span>
                    </div>

                    <div
                    v-if="request.updatedAt"
                    class="activity-block updated"
                    >
                    <span class="activity-label">
                        Updated
                    </span>

                    <strong>
                        {{ relativeDate(request.updatedAt) }}
                    </strong>

                    <span class="exact-date">
                        {{ formatCompactDate(request.updatedAt) }}
                    </span>
                    </div>
                </td>

                <td>
                    <span
                    class="status-pill"
                    :class="statusClass(request.status)"
                    >
                    {{ statusLabel(request.status) }}
                    </span>
                </td>

                <td>
                    {{
                    request.jurisdiction ||
                    "Not provided"
                    }}
                </td>

                <td>
                    <div
                    v-if="primaryIssue(request)"
                    class="issue-content"
                    >
                    <div class="issue-heading">
                        <span class="issue-topic">
                        {{ primaryIssue(request).topic }}
                        </span>

                        <span class="confidence-pill">
                        {{
                            formatProbability(
                            primaryIssue(request).probabilityScore
                            )
                        }}
                        </span>
                    </div>

                    <p class="issue-summary">
                        {{
                        shortenedSummary(
                            primaryIssue(request).summary
                        )
                        }}
                    </p>

                    <span
                        v-if="request.issues.length > 1"
                        class="additional-issues"
                    >
                        +{{ request.issues.length - 1 }}
                        more
                    </span>
                    </div>

                    <span
                    v-else
                    class="missing-value"
                    >
                    No identified issues
                    </span>
                </td>

                <td class="client-column">
                    <div class="client-pills">

                    <span
                        v-for="method in request.preferredContactMethods"
                        :key="method"
                        class="contact-pill"
                    >
                        {{ contactLabel(method) }}
                    </span>

                    <span
                        v-if="request.allowOutsideAttorneys"
                        class="outside-pill"
                    >
                        Outside allowed
                    </span>

                    </div>

                </td>
                </tr>
          </tbody>
  
        </table>
      </div>
  
    </section>
  </template>
  
  <script>
  import {
    computed,
    onMounted,
    ref,
    watch
  } from "vue";
  
  import {
    fetchAttorneyRequests
  } from "@/firebase/firestore";
  
  const STATUS_ORDER = [
    "new",
    "reviewing",
    "needs-information",
    "referred",
    "accepted",
    "closed"
  ];
  
  export default {
    name:
      "RequestDetails",
  
    props: {
      initialStatus: { type: String, default: "" },
      refreshKey: {
        type:
          Number,
        default:
          0
      }
    },
  
    emits: [
      "review", "new-count"
    ],
  
    setup(props, { emit }) {
  
      const requests =
        ref([]);
  
      const loading =
        ref(false);
  
      const errorMessage =
        ref("");
  
        const searchText =
        ref("");

        const dateSort =
        ref("newest");

        const activitySort =
        ref("");

        const statusFilter =
        ref(props.initialStatus);

        const hasActiveFilters =
        computed(
          () =>
            dateSort.value !== "newest" ||
            statusFilter.value !== "" ||
            activitySort.value !== ""
        );

        const availableStatuses = [
        {
            value: "new",
            label: "New"
        },
        {
            value: "reviewing",
            label: "Reviewing"
        },
        {
            value: "needs-information",
            label: "Needs Information"
        },
        {
            value: "referred",
            label: "Referred"
        },
        {
            value: "accepted",
            label: "Accepted"
        },
        {
            value: "closed",
            label: "Closed"
        }
        ];
  
        const normalizeStatus =
        status => {

            if (
            typeof status !== "string" ||
            status.trim() === ""
            ) {
            return "new";
            }

            const normalized =
            status
                .trim()
                .toLowerCase();

            return normalized === "submitted"
            ? "new"
            : normalized;
        };
  
      const dateValue =
        value => {
  
          if (!value) {
            return 0;
          }
  
          if (
            typeof value.toDate ===
            "function"
          ) {
  
            return value
              .toDate()
              .getTime();
          }
  
          const date =
            new Date(value);
  
          return Number.isNaN(
            date.getTime()
          )
            ? 0
            : date.getTime();
        };
  
      const loadRequests =
        async () => {
  
          if (loading.value) {
            return;
          }
  
          loading.value =
            true;
  
          errorMessage.value =
            "";
  
          try {
  
            const loadedRequests =
              await fetchAttorneyRequests();
  
            emit("new-count", loadedRequests.filter(request => normalizeStatus(request.status) === "new").length);
            requests.value =
              loadedRequests.map(
                request => ({
                  ...request,
                  status:
                    normalizeStatus(
                      request.status
                    )
                })
              );
  
          } catch (error) {
  
            console.error(
              "❌ Unable to load attorney requests:",
              error
            );
  
            errorMessage.value =
              error?.message ??
              "An unknown error occurred.";
  
          } finally {
  
            loading.value =
              false;
          }
        };
  
        const displayedRequests =
        computed(() => {

            const search =
            searchText.value
                .trim()
                .toLowerCase();

            let result =
            requests.value.filter(
                request => {

                const normalizedStatus =
                    normalizeStatus(
                    request.status
                    );

                if (
                    statusFilter.value &&
                    normalizedStatus !==
                    statusFilter.value
                ) {
                    return false;
                }

                if (
                    !statusFilter.value &&
                    normalizedStatus === "closed"
                ) {
                    return false;
                }

                if (!search) {
                    return true;
                }

                const issuesText =
                    Array.isArray(request.issues)
                    ? request.issues
                        .map(
                            issue =>
                            `${issue.topic ?? ""} ${issue.summary ?? ""}`
                        )
                        .join(" ")
                    : "";

                const searchableText = [
                    request.id,
                    request.clientId,
                    request.sourceChatId,
                    request.jurisdiction,
                    normalizedStatus,
                    issuesText,
                    ...(request.preferredContactMethods ?? [])
                ]
                    .join(" ")
                    .toLowerCase();

                return searchableText.includes(
                    search
                );
                }
            );

            result =
            [...result].sort(
                (
                first,
                second
                ) => {

                /*
                When selected, updated activity takes priority.
                Requests without updatedAt fall back to createdAt.
                */

                if (activitySort.value) {

                    const firstActivity =
                    dateValue(
                        first.updatedAt ??
                        first.createdAt
                    );

                    const secondActivity =
                    dateValue(
                        second.updatedAt ??
                        second.createdAt
                    );

                    const activityDifference =
                    activitySort.value ===
                        "recent"
                        ? secondActivity -
                        firstActivity
                        : firstActivity -
                        secondActivity;

                    if (
                    activityDifference !== 0
                    ) {
                    return activityDifference;
                    }
                }

                const firstCreated =
                    dateValue(
                    first.createdAt
                    );

                const secondCreated =
                    dateValue(
                    second.createdAt
                    );

                return dateSort.value ===
                    "oldest"
                    ? firstCreated -
                        secondCreated
                    : secondCreated -
                        firstCreated;
                }
            );

            return result;
        });

        const dateFromValue =
  value => {

    if (!value) {
      return null;
    }

    const date =
      typeof value.toDate ===
        "function"
        ? value.toDate()
        : new Date(value);

    return Number.isNaN(
      date.getTime()
    )
      ? null
      : date;
  };

const relativeDate =
  value => {

    const date =
      dateFromValue(
        value
      );

    if (!date) {
      return "Unknown";
    }

    const now =
      new Date();

    const difference =
      now.getTime() -
      date.getTime();

    const minutes =
      Math.floor(
        difference /
        60_000
      );

    const hours =
      Math.floor(
        difference /
        3_600_000
      );

    const days =
      Math.floor(
        difference /
        86_400_000
      );

    if (minutes < 1) {
      return "Just now";
    }

    if (minutes < 60) {
      return `${minutes}m ago`;
    }

    if (hours < 24) {
      return `${hours}h ago`;
    }

    if (days === 1) {
      return "Yesterday";
    }

    if (days < 7) {
      return `${days} days ago`;
    }

    if (days < 30) {
      const weeks =
        Math.floor(
          days / 7
        );

      return `${weeks} ${
        weeks === 1
          ? "week"
          : "weeks"
      } ago`;
    }

    if (days < 365) {
      const months =
        Math.floor(
          days / 30
        );

      return `${months} ${
        months === 1
          ? "month"
          : "months"
      } ago`;
    }

    const years =
      Math.floor(
        days / 365
      );

    return `${years} ${
      years === 1
        ? "year"
        : "years"
    } ago`;
  };

const contactLabel =
  method => {

    const normalized =
      String(
        method ?? ""
      ).toLowerCase();

    switch (normalized) {

    case "text":
    case "sms":
      return "Text";

    case "phone":
    case "call":
      return "Phone";

    case "email":
      return "Email";

    case "app":
      return "App";

    default:
      return method;
    }
  };

    const formatCompactDate =
    value => {

        const date =
        dateFromValue(
            value
        );

        if (!date) {
        return "";
        }

        return date.toLocaleString(
        undefined,
        {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit"
        }
        );
    };
  
      const statusSummaries =
        computed(() => {
  
          const counts =
            new Map();
  
          for (
            const request of
            requests.value
          ) {
  
            const status =
              normalizeStatus(
                request.status
              );
  
            counts.set(
              status,
              (counts.get(status) ?? 0) +
                1
            );
          }
  
          return STATUS_ORDER
            .filter(
              status =>
                counts.has(status)
            )
            .map(
              status => ({
                status,
                count:
                  counts.get(status)
              })
            );
        });
  
        const toggleStatusFilter =
  status => {

    statusFilter.value =
      statusFilter.value === status
        ? ""
        : status;
  };
  
      const statusLabel =
        status => {
  
          switch (
            normalizeStatus(status)
          ) {
  
          case "new":
            return "New";
  
          case "reviewing":
            return "Reviewing";
  
          case "needs-information":
            return "Needs Information";
  
          case "referred":
            return "Referred";
  
          case "accepted":
            return "Accepted";
  
          case "closed":
            return "Closed";
  
          default:
  
            return String(status)
              .replaceAll("-", " ")
              .replace(
                /\b\w/g,
                letter =>
                  letter.toUpperCase()
              );
          }
        };
  
      const statusClass =
        status => {
  
          return `status-${normalizeStatus(
            status
          )}`;
        };
  
      const primaryIssue =
        request => {
  
          if (
            !Array.isArray(
              request.issues
            ) ||
            request.issues.length ===
              0
          ) {
  
            return null;
          }
  
          return [...request.issues]
            .sort(
              (
                first,
                second
              ) =>
                (
                  second.probabilityScore ??
                  0
                ) -
                (
                  first.probabilityScore ??
                  0
                )
            )[0];
        };
  
      const shortenedSummary =
        summary => {
  
          const value =
            String(
              summary ??
              ""
            ).trim();
  
          if (
            value.length <=
            150
          ) {
  
            return value;
          }
  
          return `${value.slice(
            0,
            147
          )}...`;
        };
  
      const formatProbability =
        probability => {
  
          const value =
            Number(probability);
  
          if (
            Number.isNaN(value)
          ) {
  
            return "0%";
          }
  
          return `${Math.round(
            value * 100
          )}%`;
        };
  
      onMounted(
        loadRequests
      );
  
      watch(
        () =>
          props.refreshKey,
        loadRequests
      );
  
      return {
        requests,
        loading,
        errorMessage,

        searchText,
        dateSort,
        activitySort,
        statusFilter,
        hasActiveFilters,
        availableStatuses,

        displayedRequests,
        statusSummaries,

        loadRequests,
        toggleStatusFilter,
        statusLabel,
        statusClass,
        primaryIssue,
        shortenedSummary,
        formatProbability,

        relativeDate,
        formatCompactDate,
        contactLabel
        };
    }
  };
  </script>
  
  <style scoped>
  .request-details {
    display: flex;
    flex-direction: column;
    gap: 20px;
    min-height: 0;
  }
  
  /* Toolbar */
  
  .request-toolbar {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
  }
  
  .toolbar-copy h3 {
    margin: 0;
    color: #1c2923;
    font-size: 1.15rem;
  }
  
  .toolbar-copy p {
    max-width: 680px;
    margin: 5px 0 0;
    color: #747e79;
    line-height: 1.45;
  }
  
  .toolbar-controls {
    display: flex;
    align-items: flex-end;
    width: 100%;
    flex-shrink: 0;
    gap: 10px;
  }
  
  .future-search {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 230px;
    padding: 0 11px;
    border: 1px solid #dde2df;
    border-radius: 9px;
    background: #f4f6f5;
    color: #969f9a;
  }
  
  .future-search span {
    font-size: 20px;
  }
  
  .future-search input {
    width: 100%;
    padding: 10px 0;
    border: 0;
    outline: 0;
    background: transparent;
    color: #969f9a;
    font: inherit;
  }
  
  .sort-control {
    display: flex;
    flex-direction: column;
    gap: 5px;
    color: #59645f;
    font-size: 0.78rem;
    font-weight: 700;
  }
  
  .sort-control select {
    min-width: 145px;
    padding: 9px 11px;
    border: 1px solid #d7ded9;
    border-radius: 9px;
    background: #ffffff;
    color: #25322c;
    font: inherit;
  }
  
  .refresh-button {
    margin-left: auto;
    padding: 10px 15px;
    border: 1px solid #d9ebe0;
    border-radius: 9px;
    background: #f2f8f4;
    color: #159c62;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }
  
  .refresh-button:hover:not(:disabled) {
    border-color: #c9e3d4;
    background: #e8f4ec;
  }

  .refresh-button--filtered {
    border-color: #178554;
    background: #178554;
    color: #ffffff;
  }

  .refresh-button--filtered:hover:not(:disabled) {
    border-color: #116d45;
    background: #116d45;
  }
  
  .refresh-button:disabled {
    cursor: wait;
    opacity: 0.65;
  }
  
  /* Status summary */
  
  .status-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  
  .summary-card {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 10px 13px;
    border: 1px solid #dfe5e1;
    border-radius: 10px;
    background: #ffffff;
    color: #34413a;
    font: inherit;
    cursor: pointer;
  }
  
  .summary-card:hover {
    background: #f7faf8;
  }
  
  .summary-card.active {
    border-color: #9bcdb4;
    background: #edf7f1;
    color: #178554;
  }
  
  .summary-label {
    font-size: 0.86rem;
    font-weight: 600;
  }
  
  .summary-count {
    font-size: 1rem;
  }
  
  .clear-filter-button {
    padding: 10px 13px;
    border: 0;
    background: transparent;
    color: #69736e;
    font: inherit;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
  }
  
  /* States */
  
  .state-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 24px;
    border: 1px solid #e2e7e4;
    border-radius: 12px;
    background: #ffffff;
    color: #737d78;
  }
  
  .state-card.error {
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
    border-color: #efc5c5;
    background: #fff7f7;
    color: #a33636;
  }
  
  .state-card.empty {
    min-height: 120px;
  }
  
  .state-card h4 {
    margin: 0;
    color: #25322c;
  }
  
  .state-card p {
    margin: 5px 0 0;
  }
  
  .empty-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    flex-shrink: 0;
    border-radius: 12px;
    background: #edf7f1;
    color: #178554;
  }
  
  /* Table */
  
  .request-table-wrapper {
    overflow-x: auto;
    border: 1px solid #e2e7e4;
    border-radius: 12px;
    background: #ffffff;
  }
  
  .request-table {
    width: 100%;
    border-collapse: collapse;
  }
  
  .request-table th,
  .request-table td {
    padding: 16px 18px;
    border-bottom: 1px solid #edf0ee;
    text-align: left;
    vertical-align: top;
  }
  
  .request-table th {
    background: #f7f9f8;
    color: #59645f;
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  
  .request-table tbody tr:last-child td {
    border-bottom: 0;
  }
  
  .request-table tbody tr:hover {
    background: #fbfdfb;
  }
  
  .received-column {
    min-width: 160px;
  }
  
  .received-date,
  .client-id,
  .issue-topic {
    color: #1c2923;
    font-weight: 600;
  }
  
  .request-id,
  .source-chat {
    max-width: 220px;
    margin-top: 5px;
    overflow: hidden;
    color: #969f9a;
    font-size: 0.74rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .source-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }
  
  .contact-pill,
  .outside-pill,
  .confidence-pill,
  .additional-issues {
    display: inline-flex;
    padding: 4px 8px;
    border-radius: 999px;
    font-size: 0.73rem;
    font-weight: 600;
  }
  
  .contact-pill {
    background: #edf3f8;
    color: #386780;
  }
  
  .outside-pill {
    background: #f8f3e7;
    color: #8b6b1f;
  }
  
  .issue-content {
    min-width: 280px;
    max-width: 520px;
  }
  
  .issue-heading {
    display: flex;
    align-items: center;
    gap: 9px;
  }
  
  .confidence-pill {
    background: #edf7f1;
    color: #178554;
  }
  
  .issue-summary {
    margin: 6px 0 0;
    color: #66716b;
    font-size: 0.87rem;
    line-height: 1.42;
  }
  
  .additional-issues {
    margin-top: 8px;
    background: #f1f2f1;
    color: #68716d;
  }
  
  .missing-value {
    color: #919a95;
    font-size: 0.85rem;
  }
  
  /* Status pills */
  
  .status-pill {
    display: inline-flex;
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 700;
    white-space: nowrap;
  }
  
  .status-new {
    background: #eaf2ff;
    color: #2f67a8;
  }
  
  .status-reviewing {
    background: #fff5db;
    color: #8c6817;
  }
  
  .status-needs-information {
    background: #fff0e8;
    color: #a35220;
  }
  
  .status-referred {
    background: #f0ebff;
    color: #684aa0;
  }
  
  .status-accepted {
    background: #edf7f1;
    color: #178554;
  }
  
  .status-closed {
    background: #f1f2f1;
    color: #66706b;
  }
  
  .review-button {
    padding: 8px 13px;
    border: 1px solid #c8ded2;
    border-radius: 8px;
    background: #edf7f1;
    color: #178554;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }
  
  .review-button:hover {
    background: #dff1e7;
  }

  .request-toolbar {
    display: flex;
    flex-direction: column;
    gap: 18px;
    }

    .toolbar-copy h3 {
    margin: 0;
    color: #1c2923;
    font-size: 1.15rem;
    }

    .toolbar-copy p {
    max-width: 760px;
    margin: 5px 0 0;
    color: #747e79;
    line-height: 1.45;
    }

    .search-row {
    width: 100%;
    }

    .request-search {
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    max-width: 720px;
    padding: 0 13px;
    box-sizing: border-box;
    border: 1px solid #d7ded9;
    border-radius: 10px;
    background: #ffffff;
    color: #8a948f;
    }

    .request-search span {
    font-size: 21px;
    }

    .request-search input {
    width: 100%;
    padding: 12px 0;
    border: 0;
    outline: 0;
    background: transparent;
    color: #25322c;
    font: inherit;
    }

    .toolbar-controls {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 12px;
    }

    .sort-control {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 170px;
    color: #59645f;
    font-size: 0.78rem;
    font-weight: 700;
    }

    .sort-control select {
    width: 100%;
    padding: 10px 11px;
    border: 1px solid #d7ded9;
    border-radius: 9px;
    background: #ffffff;
    color: #25322c;
    font: inherit;
    }

    .actions-column {
  width: 90px;
}

.activity-column {
  width: 150px;
  min-width: 145px;
}

.activity-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.activity-block.updated {
  margin-top: 12px;
}

.activity-label {
  color: #8b9590;
  font-size: 0.69rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.activity-block strong {
  color: #25322c;
  font-size: 0.86rem;
}

.exact-date {
  color: #8c9691;
  font-size: 0.71rem;
  line-height: 1.3;
}

.client-column {
  min-width: 190px;
}

.client-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

  @media (max-width: 1000px) {
    .request-toolbar {
      align-items: stretch;
      flex-direction: column;
    }
  
    .toolbar-controls {
      flex-wrap: wrap;
    }
  
    .future-search {
      flex: 1;
    }
  }
  
  @media (max-width: 650px) {
    .toolbar-controls {
      align-items: stretch;
      flex-direction: column;
    }
  
    .future-search,
    .sort-control select,
    .refresh-button {
      width: 100%;
      box-sizing: border-box;
    }

    .refresh-button {
      margin-left: 0;
    }
  }
  </style>
